import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { type ReactNode, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { AUTH_CODE_PATH } from "~/components/auth/auth-flow";
import { LoginScreen } from "~/components/auth/LoginScreen";
import { VerifyScreen } from "~/components/auth/VerifyScreen";
import { UNAUTHORIZED_EVENT } from "~/lib/auth-token";
import { LEGAL_ROUTE_PATH } from "~/lib/legal";
import { LIVE_ROUTE_PATH } from "~/lib/live-links";
import { MEMBERSHIP_REQUIRED_EVENT } from "~/lib/membership";
import { InviteRoute } from "~/routes/InviteRoute";
import { LegalRoute } from "~/routes/LegalRoute";
import { LiveEventRoute } from "~/routes/LiveEventRoute";
import { useAuthStore } from "~/store/auth-store";

/**
 * Decides between the sign-in screen and the app.
 *
 * Three jobs: run the one-time boot hydration, and listen for the two global events
 * the API client dispatches — "session is gone" (401) and "membership required"
 * (403). Routing these through events rather than direct store imports keeps
 * `lib/api.ts` free of any dependency on the store.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useAuthStore((state) => state.status);
  const hydrate = useAuthStore((state) => state.hydrate);
  const handleUnauthorized = useAuthStore((state) => state.handleUnauthorized);
  const markMembershipRequired = useAuthStore(
    (state) => state.markMembershipRequired,
  );

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    const listener = () => handleUnauthorized();
    window.addEventListener(UNAUTHORIZED_EVENT, listener);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, listener);
  }, [handleUnauthorized]);

  useEffect(() => {
    const listener = () => markMembershipRequired();
    window.addEventListener(MEMBERSHIP_REQUIRED_EVENT, listener);
    return () =>
      window.removeEventListener(MEMBERSHIP_REQUIRED_EVENT, listener);
  }, [markMembershipRequired]);

  useEffect(() => {
    if (status !== "unauthenticated") return;
    const search = new URLSearchParams(location.search);
    if (search.get("chat") !== "open") return;
    search.delete("chat");
    const nextSearch = search.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, status]);

  if (status === "loading") {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          // Same surface as the sign-in screen that follows it, so the hand-off from
          // the spinner is not a flash of a different colour.
          backgroundColor: "background.paper",
        }}
      >
        <CircularProgress size={28} thickness={4} />
      </Box>
    );
  }

  if (status === "unauthenticated")
    return (
      <Routes>
        {/* The code step is a route, so entering it is a navigation the back control
            and Android's hardware button can undo. */}
        <Route path={AUTH_CODE_PATH} element={<VerifyScreen />} />
        <Route path="/invite/:token" element={<InviteRoute />} />
        {/* Public by requirement: the terms have to be readable before there is an
            account, and an app store reviewer arrives here from a store listing. */}
        <Route path={LEGAL_ROUTE_PATH} element={<LegalRoute />} />
        {/* The webinar registration page: an outsider arrives from a marketing link
            or a confirmation e-mail with no account at all. */}
        <Route path={LIVE_ROUTE_PATH} element={<LiveEventRoute />} />
        {/* Everything else is the e-mail step, at whatever URL the app was on — a
            session that expires deep in the app does not rewrite the address bar on
            its way out. Once the session exists, the app's own routes take over. */}
        <Route path="*" element={<LoginScreen />} />
      </Routes>
    );

  return <>{children}</>;
}
